import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Heart, Package, CreditCard, MapPin, User, Mail, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { addMonths, format } from "date-fns";
import { fr } from "date-fns/locale";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getOrderData, saveOrderData, clearOrderData, type SelectedActivity } from "@/lib/storage";
import { supabase } from "@/integrations/supabase/client";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableActivityItem } from '@/components/SortableActivityItem';

const Checkout = () => {
  const [orderType, setOrderType] = useState<"custom" | "curated">("custom");
  const [frequency, setFrequency] = useState("3");
  const [selectedActivities, setSelectedActivities] = useState<SelectedActivity[]>([]);
  const [description, setDescription] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  
  // Formulaire
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [recipientLastName, setRecipientLastName] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [payerFirstName, setPayerFirstName] = useState("");
  const [payerLastName, setPayerLastName] = useState("");
  const [email, setEmail] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  
  // Code promo
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    reduction: number;
    type: 'percentage' | 'fixed';
    is_recurring: boolean;
  } | null>(null);
  const [promoError, setPromoError] = useState("");
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);

  // Code de parrainage
  const [referralCode, setReferralCode] = useState("");
  const [appliedReferral, setAppliedReferral] = useState<{
    code: string;
    referrer_user_id: string;
  } | null>(null);
  const [referralError, setReferralError] = useState("");
  const [isCheckingReferral, setIsCheckingReferral] = useState(false);

  // Calculs de dates
  const getFirstBoxMonth = () => {
    const nextMonth = addMonths(new Date(), 1);
    return format(nextMonth, "MMMM yyyy", { locale: fr });
  };

  const getFirstBoxDate = () => {
    return addMonths(new Date(), 1);
  };

  const getReminderDate = () => {
    const firstBoxDate = addMonths(new Date(), 1);
    // Rappel un mois plus tôt que prévu (nombre d'activités - 1 mois)
    const reminderDate = addMonths(firstBoxDate, selectedActivities.length - 1);
    return format(reminderDate, "MMMM yyyy", { locale: fr });
  };

  const getReminderDateForCurated = () => {
    const firstBoxDate = addMonths(new Date(), 1);
    // Pour curated, on assume 3 mois puis rappel (3-1 = 2 mois après première box)
    const reminderDate = addMonths(firstBoxDate, 2);
    return format(reminderDate, "MMMM yyyy", { locale: fr });
  };

  // Fonction pour vérifier et appliquer le code promo
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Veuillez entrer un code promo");
      return;
    }

    setIsCheckingPromo(true);
    setPromoError("");

    try {
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: { code: promoCode.trim() }
      });

      if (error) throw error;

      if (data.valid) {
        setAppliedPromo({
          code: promoCode.trim().toUpperCase(),
          reduction: Number(data.reduction),
          type: data.type_reduction as 'percentage' | 'fixed',
          is_recurring: data.is_recurring
        });
        setPromoError("");
      } else {
        setPromoError(data.message || "Code promo invalide ou expiré");
        setAppliedPromo(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du code promo:", error);
      setPromoError("Erreur lors de la vérification du code promo");
      setAppliedPromo(null);
    } finally {
      setIsCheckingPromo(false);
    }
  };

  // Fonction pour vérifier et appliquer le code de parrainage
  const handleApplyReferralCode = async () => {
    if (!referralCode.trim()) {
      setReferralError("Veuillez entrer un code de parrainage");
      return;
    }

    setIsCheckingReferral(true);
    setReferralError("");

    try {
      const { data, error } = await supabase.functions.invoke('validate-referral-code', {
        body: { code: referralCode.trim() }
      });

      if (error) throw error;

      if (data.valid) {
        setAppliedReferral({
          code: referralCode.trim().toUpperCase(),
          referrer_user_id: data.referrer_user_id
        });
        setReferralError("");
      } else {
        setReferralError(data.message || "Code de parrainage invalide");
        setAppliedReferral(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du code de parrainage:", error);
      setReferralError("Erreur lors de la vérification du code de parrainage");
      setAppliedReferral(null);
    } finally {
      setIsCheckingReferral(false);
    }
  };

  // Calcul du total avec réduction
  const basePrice = 29.50;
  const deliveryPrice = 2.99;
  const subtotal = basePrice + deliveryPrice;
  
  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    
    if (appliedPromo.type === 'percentage') {
      return (subtotal * appliedPromo.reduction) / 100;
    } else {
      return appliedPromo.reduction;
    }
  };
  
  const discount = calculateDiscount();
  const totalPrice = Math.max(0, subtotal - discount);

  useEffect(() => {
    // Charger les données sauvegardées au démarrage
    const orderData = getOrderData();
    if (orderData) {
      setOrderType(orderData.orderType);
      setSelectedActivities(orderData.selectedActivities || []);
      setFrequency(orderData.frequency?.toString() || "3");
      setDescription(orderData.description || "");
    }

    // Vérifier si l'URL contient le paramètre mode=curated
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mode') === 'curated') {
      setOrderType('curated');
      saveOrderData({
        orderType: 'curated',
        selectedActivities: [],
        description: ""
      });
    }
  }, []);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSelectedActivities((items) => {
        const oldIndex = items.findIndex(item => item.id.toString() === active.id);
        const newIndex = items.findIndex(item => item.id.toString() === over.id);
        
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Sauvegarder le nouvel ordre
        saveOrderData({
          orderType,
          selectedActivities: newOrder,
          description
        });
        
        return newOrder;
      });
    }
  };

  const handleRemoveActivity = (index: number) => {
    const newActivities = selectedActivities.filter((_, i) => i !== index);
    setSelectedActivities(newActivities);
    
    // Sauvegarder les nouvelles activités
    saveOrderData({
      orderType,
      selectedActivities: newActivities,
      description
    });
  };

  const handleCreatePayment = async () => {
    setIsProcessing(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        alert("Vous devez être connecté pour vous abonner.");
        return;
      }

      // Make direct POST request to Stripe checkout with promo code info
      const response = await fetch('https://qudvztcondkuuiqjascx.supabase.co/functions/v1/Stripe_checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: user.email,
          amount: Math.round(totalPrice * 100), // Montant en centimes
          promoCode: appliedPromo?.code || null,
          discount: discount > 0 ? Math.round(discount * 100) : null // Réduction en centimes
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Erreur lors de la redirection vers Stripe.");
      }
    } catch (error) {
      console.error('Erreur lors de l\'abonnement:', error);
      alert("Erreur lors de la redirection vers Stripe.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">
              Finalisons votre abonnement Yumeli
            </h1>
            <p className="text-xl text-muted-foreground">
              Une dernière étape avant de faire plaisir à votre grand-parent
            </p>
          </div>

          <div className="space-y-8">
            {/* Sélection du type de commande */}
            <Card className="p-6 shadow-warm">
              <h2 className="text-xl font-semibold text-foreground mb-6">Type d'abonnement</h2>
              <div className="space-y-4">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    orderType === 'custom' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setOrderType('custom')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      orderType === 'custom' ? 'border-primary bg-primary' : 'border-muted-foreground'
                    }`}></div>
                    <div>
                      <h3 className="font-semibold">Je compose ma box</h3>
                      <p className="text-sm text-muted-foreground">Sélectionnez vous-même les activités</p>
                    </div>
                  </div>
                </div>
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    orderType === 'curated' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setOrderType('curated')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      orderType === 'curated' ? 'border-primary bg-primary' : 'border-muted-foreground'
                    }`}></div>
                    <div>
                      <h3 className="font-semibold">Yumeli s'occupe du choix des activités</h3>
                      <p className="text-sm text-muted-foreground">Nos experts sélectionnent pour vous</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Récapitulatif de commande */}
            <Card className="p-6 shadow-warm">
              {orderType === "custom" && (
                <div className="flex items-center gap-3 mb-6">
                  <Package className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">Récapitulatif de votre box</h2>
                </div>
              )}

              {orderType === "custom" ? (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 text-foreground">Activités sélectionnées (1 par box mensuelle)</h3>
                  <div className="space-y-2">
                    {selectedActivities.length > 0 ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis]}
                      >
                        <SortableContext
                          items={selectedActivities.map(activity => activity.id.toString())}
                          strategy={verticalListSortingStrategy}
                        >
                          {selectedActivities.map((activity, index) => (
                            <SortableActivityItem
                              key={activity.id}
                              activity={activity}
                              index={index}
                              onRemove={handleRemoveActivity}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <div className="p-4 bg-muted/30 rounded-lg border border-border text-center">
                        <p className="text-xl text-muted-foreground mb-4">
                          Aucune activité sélectionnée.
                        </p>
                        <Button variant="outline" asChild>
                          <a href="/activites">Retourner à la sélection</a>
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Informations de livraison */}
                  {selectedActivities.length > 0 && (
                    <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <h4 className="font-semibold text-foreground">Informations de livraison</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p className="text-foreground">
                          <span className="font-medium">Votre première box sera envoyée en :</span> {getFirstBoxMonth()}
                        </p>
                        <p className="text-muted-foreground">
                          Nous vous enverrons un rappel en <span className="font-medium">{getReminderDate()}</span> pour choisir les prochaines activités.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 text-foreground">Décrivez nous votre ainés, ses capacités et ses activités favorites !</h3>
                  <div className="p-4 bg-muted/30 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground mb-3">
                      Nos experts sélectionneront 1 activité parfaitement adaptée à votre description pour chaque box mensuelle.
                    </p>
                    <Textarea
                      id="description"
                      placeholder="Ex: Marie, 78 ans, aime les activités manuelles, a des problèmes de vue mais garde une excellente dextérité. Passionnée de cuisine et de jardinage..."
                      className="mt-2"
                      rows={6}
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        saveOrderData({
                          orderType: 'curated',
                          selectedActivities: [],
                          description: e.target.value
                        });
                      }}
                      required
                    />
                  </div>

                  {/* Informations de livraison pour curated */}
                  {description && (
                    <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <h4 className="font-semibold text-foreground">Informations de livraison</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p className="text-foreground">
                          <span className="font-medium">Votre première box sera envoyée en :</span> {getFirstBoxMonth()}
                        </p>
                        <p className="text-muted-foreground">
                          Nous vous enverrons un rappel en <span className="font-medium">{getReminderDateForCurated()}</span> pour choisir les prochaines activités.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}


              <Separator className="my-6" />
              
                <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Box mensuelle (1 activité par box)</span>
                  <span className="font-semibold">29,50 €</span>
                </div>
                <div className="flex justify-between">
                  <span>Livraison</span>
                  <span className="text-foreground font-semibold">2,99 €</span>
                </div>
                
                {/* Code promo */}
                <div className="pt-3 space-y-2">
                  <Label htmlFor="promoCode" className="text-sm font-medium">Code promo</Label>
                  <div className="flex gap-2">
                    <Input
                      id="promoCode"
                      placeholder="Entrez votre code"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoError("");
                      }}
                      disabled={isCheckingPromo || !!appliedPromo}
                      className="flex-1"
                    />
                    {!appliedPromo ? (
                      <Button 
                        type="button"
                        onClick={handleApplyPromoCode}
                        disabled={isCheckingPromo || !promoCode.trim()}
                        variant="outline"
                      >
                        {isCheckingPromo ? "..." : "Appliquer"}
                      </Button>
                    ) : (
                      <Button 
                        type="button"
                        onClick={() => {
                          setAppliedPromo(null);
                          setPromoCode("");
                        }}
                        variant="outline"
                      >
                        Retirer
                      </Button>
                    )}
                  </div>
                  {promoError && (
                    <p className="text-xs text-destructive">{promoError}</p>
                  )}
                  {appliedPromo && (
                    <p className="text-xs text-green-600 font-medium">
                      Code promo appliqué : {appliedPromo.type === 'percentage' 
                        ? `${appliedPromo.reduction}% de réduction`
                        : `${appliedPromo.reduction.toFixed(2)} € de réduction`
                      }
                    </p>
                  )}
                </div>
                
                {appliedPromo && (
                  <div className="flex justify-between text-green-600">
                    <span>Réduction</span>
                    <span className="font-semibold">-{discount.toFixed(2)} €</span>
                  </div>
                )}
                
                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total par mois</span>
                  <span className="text-foreground">{totalPrice.toFixed(2)} €</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Sans engagement • Résiliable à tout moment
                </p>
              </div>
            </Card>

            {/* Message informatif */}
            <Card className="p-4 shadow-warm">
              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-base text-foreground text-center">
                  Une fois toutes les activités envoyées, nous vous préviendrons pour que vous puissiez en choisir de nouvelles !
                </p>
              </div>
            </Card>

            {/* Formulaire de commande */}
            <Card className="p-6 shadow-warm">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold text-foreground">Informations de commande</h2>
              </div>

              <form className="space-y-6">
                {/* Informations du destinataire */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <User className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Destinataire de la box</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="recipientFirstName">Prénom *</Label>
                      <Input 
                        id="recipientFirstName" 
                        value={recipientFirstName}
                        onChange={(e) => setRecipientFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="recipientLastName">Nom *</Label>
                      <Input 
                        id="recipientLastName" 
                        value={recipientLastName}
                        onChange={(e) => setRecipientLastName(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Adresse de livraison */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Adresse de livraison</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address">Adresse *</Label>
                      <Input 
                        id="address" 
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="addressComplement">Complément d'adresse</Label>
                      <Input 
                        id="addressComplement" 
                        
                        value={addressComplement}
                        onChange={(e) => setAddressComplement(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="postalCode">Code postal *</Label>
                        <Input 
                          id="postalCode" 
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          pattern="[0-9]{5}"
                          title="Le code postal doit contenir 5 chiffres"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">Ville *</Label>
                        <Input 
                          id="city" 
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations de facturation */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Vos informations</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="payerFirstName">Votre prénom *</Label>
                      <Input 
                        id="payerFirstName" 
                        value={payerFirstName}
                        onChange={(e) => setPayerFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="payerLastName">Votre nom *</Label>
                      <Input 
                        id="payerLastName" 
                        value={payerLastName}
                        onChange={(e) => setPayerLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                        title="Veuillez entrer une adresse email valide"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pour recevoir les confirmations et gérer votre abonnement
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="phoneNumber">Numéro de téléphone *</Label>
                      <Input 
                        id="phoneNumber" 
                        type="tel" 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        
                        pattern="^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$"
                        title="Veuillez entrer un numéro de téléphone français valide"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pour vous contacter en cas de problème de livraison
                      </p>
                    </div>
                  </div>
                </div>

                {/* Code de parrainage */}
                <div>
                  <h3 className="font-semibold text-foreground mb-4">Code de parrainage</h3>
                  <div className="space-y-2">
                    <Label htmlFor="referralCode" className="text-sm font-medium">
                      Vous avez été parrainé ? Entrez le code ici
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="referralCode"
                        placeholder="Entrez le code de parrainage"
                        value={referralCode}
                        onChange={(e) => {
                          setReferralCode(e.target.value.toUpperCase());
                          setReferralError("");
                        }}
                        disabled={isCheckingReferral || !!appliedReferral}
                        className="flex-1"
                      />
                      {!appliedReferral ? (
                        <Button 
                          type="button"
                          onClick={handleApplyReferralCode}
                          disabled={isCheckingReferral || !referralCode.trim()}
                          variant="outline"
                        >
                          {isCheckingReferral ? "..." : "Vérifier"}
                        </Button>
                      ) : (
                        <Button 
                          type="button"
                          onClick={() => {
                            setAppliedReferral(null);
                            setReferralCode("");
                          }}
                          variant="outline"
                        >
                          Retirer
                        </Button>
                      )}
                    </div>
                    {referralError && (
                      <p className="text-xs text-destructive">{referralError}</p>
                    )}
                    {appliedReferral && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 font-medium">
                          ✨ Code valide ! Un cadeau sera ajouté à votre première box
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Paiement */}
                <div>
                  <h3 className="font-semibold text-foreground mb-4">Informations de paiement</h3>
                  <div className="p-4 bg-muted/30 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground text-center">
                      🔒 Paiement sécurisé par Stripe
                      <br />
                      Vos informations bancaires sont protégées
                    </p>
                  </div>
                </div>

                <Button
  className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
  size="lg"
  disabled={isProcessing}
  onClick={async () => {
    setIsProcessing(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        alert("Vous devez être connecté pour vous abonner.");
        return;
      }

      // Validation des champs requis
      if (!recipientFirstName || !recipientLastName || !address || !postalCode || !city || !payerFirstName || !payerLastName || !email || !phoneNumber) {
        alert("Veuillez remplir tous les champs requis.");
        setIsProcessing(false);
        return;
      }

      // Validation format email
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      if (!emailRegex.test(email)) {
        alert("Veuillez entrer une adresse email valide.");
        setIsProcessing(false);
        return;
      }

      // Validation format code postal
      const postalCodeRegex = /^[0-9]{5}$/;
      if (!postalCodeRegex.test(postalCode)) {
        alert("Le code postal doit contenir exactement 5 chiffres.");
        setIsProcessing(false);
        return;
      }

      // Validation format numéro de téléphone
      const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
      if (!phoneRegex.test(phoneNumber.replace(/\s/g, ''))) {
        alert("Veuillez entrer un numéro de téléphone français valide.");
        setIsProcessing(false);
        return;
      }

      // Validation spécifique pour le mode curated
      if (orderType === 'curated' && !description.trim()) {
        alert("Veuillez décrire votre grand-parent pour que nous puissions choisir les bonnes activités.");
        setIsProcessing(false);
        return;
      }

      // Validation spécifique pour le mode custom - vérifier qu'au moins une activité est sélectionnée
      if (orderType === 'custom' && selectedActivities.length === 0) {
        alert("Aucune activité sélectionnée. Veuillez sélectionner au moins une activité ou retourner à la page des activités.");
        setIsProcessing(false);
        return;
      }

      // Ajouter l'ordre à chaque activité avant de sauvegarder
      const activitiesWithOrder = selectedActivities.map((activity, index) => ({
        ...activity,
        order: index + 1
      }));

      // Extraire l'URL de la photo personnalisée pour le puzzle (ID: 1)
      const puzzleActivity = selectedActivities.find(activity => activity.id === 1);
      const customPhotoUrl = puzzleActivity?.customPhotoUrl || null;

      // Calculer les dates pour la base de données
      const firstBoxDate = getFirstBoxDate();
      const reminderDate = orderType === 'custom' 
        ? addMonths(firstBoxDate, selectedActivities.length - 1)
        : addMonths(firstBoxDate, 2); // Pour curated, rappel après 2 mois

      // Sauvegarder les informations de commande dans Supabase
      const orderData = {
        user_id: user.id,
        order_type: orderType,
        selected_activities: activitiesWithOrder as any, // Convertir en JSON avec ordre
        description: orderType === 'curated' ? description : null,
        recipient_first_name: recipientFirstName,
        recipient_last_name: recipientLastName,
        delivery_address: address,
        postal_code: postalCode,
        city: city,
        payer_first_name: payerFirstName,
        payer_last_name: payerLastName,
        email: email,
        amount: 3249, // 32,49 € en centimes
        currency: 'eur',
        status: 'pending',
        first_box_delivery_date: firstBoxDate.toISOString().split('T')[0], // Format YYYY-MM-DD
        reminder_date: reminderDate.toISOString().split('T')[0], // Format YYYY-MM-DD
        custom_photo_url: customPhotoUrl // Ajouter l'URL de la photo personnalisée
      };

      const { data: orderResult, error: orderError } = await supabase
        .from('orders')
        .insert(orderData)
        .select('id')
        .single();

      if (orderError) {
        console.error('Erreur lors de la sauvegarde de la commande:', orderError);
        alert('Erreur lors de la sauvegarde de la commande.');
        setIsProcessing(false);
        return;
      }

      console.log('Commande sauvegardée avec l\'ID:', orderResult.id);

      // Si un code de parrainage a été appliqué, créer l'entrée de parrainage
      if (appliedReferral) {
        const { error: referralError } = await supabase
          .from('referrals')
          .insert({
            referrer_user_id: appliedReferral.referrer_user_id,
            referred_user_id: user.id,
            referral_code: appliedReferral.code,
            status: 'active'
          });

        if (referralError) {
          console.error('Erreur lors de la sauvegarde du parrainage:', referralError);
          // On continue même si l'enregistrement du parrainage échoue
        } else {
          console.log('Parrainage enregistré avec succès');
        }
      }

      const res = await fetch("https://qudvztcondkuuiqjascx.supabase.co/functions/v1/Stripe_checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          email: user.email,
          amount: Math.round(totalPrice * 100), // Montant en centimes
          promoCode: appliedPromo?.code || null,
          discount: discount > 0 ? Math.round(discount * 100) : null, // Réduction en centimes
          isRecurring: appliedPromo?.is_recurring || false // Si le code s'applique à toutes les souscriptions
        }),
      })

      const data = await res.json()

      if (data.url) {
        window.location.href = data.url
      } else {
        alert("Erreur lors de la redirection vers Stripe.")
      }
    } catch (err) {
      console.error("Erreur dans le paiement :", err)
      alert("Une erreur est survenue lors du paiement.")
    } finally {
      setIsProcessing(false);
    }
  }}
>
  <Heart className="w-5 h-5 mr-2" />
  {isProcessing ? "Traitement en cours..." : "Finaliser mon abonnement"}
</Button>



                <p className="text-xs text-muted-foreground text-center">
                  En validant, vous acceptez nos conditions générales de vente.
                  Premier prélèvement aujourd'hui, puis tous les mois à la même date.
                  Résiliation possible à tout moment depuis votre espace client.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;
